import React, { useCallback, useMemo, useState } from 'react'
import { useRichTextEditorContext } from 'mui-tiptap'
import { useTheme } from '@mui/styles'
import {
  MenuItem,
  Box,
  Menu,
  IconButton,
} from '@mui/material'
import AddBoxIcon from '@mui/icons-material/AddBox'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import type { PlaceholderItem } from './types'
import type ThemeInterface from '@/theme-interface'
import {
  getPlaceholderMenuButtonStyles,
  getPlaceholderButtonStyles,
  getPlaceholderMenuItemStyles,
} from './placeholder-styles'
import lang from '@/lang'

export interface PlaceholderMenuButtonProps {
  placeholderTypes?: (string | PlaceholderItem)[]
  tooltipTitle?: React.ReactNode
  disabled?: boolean
}

/**
 * A dropdown menu for inserting placeholders into the editor
 */
export default function PlaceholderMenuButton({
  placeholderTypes = [],
  tooltipTitle = lang.htmlInput.tooltipLabels.placeholder,
  disabled = false,
}: PlaceholderMenuButtonProps) {
  const editor = useRichTextEditorContext()
  const theme = useTheme<ThemeInterface>()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const placeholderOptions = useMemo(() => {
    return placeholderTypes.map((placeholder) => {
      if (typeof placeholder === 'string') {
        return {
          label: placeholder,
          value: placeholder,
        }
      }
      return {
        label: placeholder.label,
        value: placeholder.value,
      }
    })
  }, [placeholderTypes])

  const handleDropdownClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handlePlaceholderSelect = useCallback(
    (value: string) => {
      if (!value || !editor) {
        return
      }

      const placeholder = placeholderTypes.find((p) =>
        typeof p === 'string' ? p === value : p.value === value,
      )

      if (!placeholder) {
        return
      }

      const placeholderValue = typeof placeholder === 'string' ? placeholder : placeholder.value
      const placeholderLabel = typeof placeholder === 'string' ? placeholder : placeholder.label

      // Check if this is an HTML placeholder (starts with < and ends with >)
      if (/^<.*>$/g.test(placeholderValue)) {
        // For HTML placeholders, use the insertHtmlPlaceholder command
        editor.commands.insertHtmlPlaceholder({
          html: placeholderValue,
          label: placeholderLabel,
        })
      } else {
        // For text placeholders, use the insertPlaceholder command
        editor.commands.insertPlaceholder({
          value: placeholderValue,
        })
      }

      editor.commands.focus()
      handleClose()
    },
    [editor, placeholderTypes, handleClose],
  )

  const isDisabled = disabled || !editor

  if (placeholderTypes.length === 0) {
    return null
  }

  return (
    <Box sx={getPlaceholderMenuButtonStyles()}>
      <IconButton
        size="small"
        onClick={handleDropdownClick}
        disabled={isDisabled}
        title={typeof tooltipTitle === 'string' ? tooltipTitle : lang.htmlInput.tooltipLabels.placeholder}
        sx={getPlaceholderButtonStyles(theme)}
      >
        <AddBoxIcon fontSize="small" />
        <ArrowDropDownIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {placeholderOptions.map(({ value, label }) => (
          <MenuItem
            key={value}
            onClick={() => handlePlaceholderSelect(value)}
            sx={getPlaceholderMenuItemStyles()}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
